import { AfterViewInit, ChangeDetectorRef, Component, Input, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { ReferrerPortalService } from '../referrer-portal.service';
import { StaticDataService } from 'src/app/services/static-data.service';
import { ReferralDataModel } from '../../configuration/referral-configuration/models/referral-data.model';
import { TdoTableSelectionEvent } from 'src/app/tdo-controls/common/tdo-table-v2/models/tdo-table-selection.event';
import { FileManagerService } from '../../file-manager/file-manager.service';
import { DocumentClassEnum } from 'src/app/models/enum/document/document-class.enum';
import { MessagingService } from 'src/app/services/messaging.service';
import { ToastrService } from 'ngx-toastr';
import { TdoOrganizerSlideshowDialogComponent } from 'src/app/main-interface/organizer/dialogs/tdo-organizer-slideshow-dialog/tdo-organizer-slideshow-dialog.component';
import { DOC_TAGS_PRE_OP, DOC_TAGS_POST_OP } from 'src/app/constants';
import { DOCUMENT_TAG } from 'src/app/models/constants/document-tag.model';

@Component({
  standalone: false,
  selector: 'app-referrer-portal-referral-detail',
  templateUrl: './referrer-portal-referral-detail.component.html',
  styleUrls: ['./referrer-portal-referral-detail.component.css']
})
export class ReferrerPortalReferralDetailComponent implements AfterViewInit, OnDestroy {

  @Input() set referral(value: ReferralDataModel){
    this.currentReferral = value;
    this.init();
  };
  currentReferral : ReferralDataModel;
  // CL-618/CL-621 follow-up magic link deep link - a DocumentID to auto-open once
  // referralReports finishes loading (see GetReferreralReports). Set by
  // ReferrerPortalHomeComponent when the referrer arrived via a "Send Link to Doctor Only"
  // emailed link; null for a normal manual referral selection.
  @Input() targetDocumentId: string = null;
  referrerId: string;
  referrerInformation: any = {};

  isLoadingPrescriptions: boolean = true;
  isLoadingReports: boolean = true;
  isLoadingNotes: boolean = true;
  isLoadingRecallNotes: boolean = true;
  isLoadingImages: boolean = true;

  referralPrescriptions: any;
  referralReports: any;
  referralNotes: any;
  referralRecallNotes: any;
  referralImages: any;
  imageThumbnails: any;
  private reportDialogRef: MatDialogRef<TdoOrganizerSlideshowDialogComponent> | null = null;
  private destroyed = false;

  LOCID = parseInt(localStorage.getItem('LOCID'));

  constructor(
    private readonly staticDataService: StaticDataService,
    private readonly referrerPortalService: ReferrerPortalService,
    private readonly dialog: MatDialog,
    private readonly fileManagerService: FileManagerService,
    private readonly messagingService: MessagingService,
    private readonly toastr: ToastrService,
  private readonly ngZone: NgZone,
  private readonly cdr: ChangeDetectorRef,
  ) {
    this.referrerInformation = JSON.parse(localStorage.getItem('storedReferrerUser'));
    this.referrerId = this.referrerInformation.authdata.doctorId;
  }

  ngAfterViewInit() {

    if (!this.staticDataService.isReady) {
      this.staticDataService.validValuesReadyEvent.subscribe({
        complete: () => {
          this.init();

        }
      });
      this.staticDataService.refreshValues();
    }
    else
      this.init();

  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  init() {
    if (this.currentReferral) {      
      this.isLoadingPrescriptions = true;
      this.isLoadingReports = true;
      this.isLoadingNotes = true;
      this.isLoadingRecallNotes = true;
      this.isLoadingImages = true;
      this.referralPrescriptions = null;
      this.referralReports = null;
      this.referralNotes = null;
      this.referralRecallNotes = null;
      this.referralImages = null;
      this.imageThumbnails =null;
      this.GetReferralPrescriptions();
      this.GetFinalReportNotes();
      this.GetRecallNotes();
      this.GetReferreralImages();
      this.GetReferreralReports();
    }
  }

  private GetReferralPrescriptions() {
    return this.referrerPortalService.getReferreralPrescriptions(this.referrerId, this.currentReferral.ReferralID, this.currentReferral.ReferralSourceType??"1").subscribe(
      result => {
        this.ngZone.run(() => {
          if (result){
            this.referralPrescriptions = result;
          }
        });
      },
      error => {
        this.ngZone.run(() => {
          this.displayMessage("Error", "Error getting Referral prescriptions.");
        });
      },
      () => {
        this.ngZone.run(() => {
          this.isLoadingPrescriptions = false;
          if (!this.destroyed) {
            this.cdr.detectChanges();
          }
        });
      }
    );
  }

  private GetReferreralImages() {
    return this.referrerPortalService.getReferreralImages(this.referrerId, this.currentReferral.ReferralID, this.currentReferral.ReferralSourceType??"1").subscribe(
      result => {
        this.ngZone.run(() => {
          if (result){
            result.forEach(image => {
              image.src = this.fileManagerService.getDocumentImageUrl(this.currentReferral.PatientId, image.DocumentFileName);
            });
            this.referralImages = result;
            //const allowedTags = new Set([...DOC_TAGS_PRE_OP, ...DOC_TAGS_POST_OP, DOCUMENT_TAG.Recall, DOCUMENT_TAG.WebShare].map(t => t.toLowerCase()));
            this.imageThumbnails = this.referralImages.filter(i =>
              i.enumDocumentClassID === DocumentClassEnum.Thumbnail 
              // &&
              // (
              //   i.isWebShare ||
              //   (i.dataDocumentTags ?? []).some((tag: any) => allowedTags.has(tag.enumDocumentTagID?.toLowerCase()))
              // )
            );
          }
        });
      },
      error => {
        this.ngZone.run(() => {
          this.displayMessage("Error", "Error getting Referral images.");
        });
      },
      () => {
        this.ngZone.run(() => {
          this.isLoadingImages = false;
          if (!this.destroyed) {
            this.cdr.detectChanges();
          }
        });
      }
    );
  }

  private GetReferreralReports() {
    return this.referrerPortalService.getReferreralReports(this.referrerId, this.currentReferral.ReferralID, this.currentReferral.ReferralSourceType??"1").subscribe(
      result => {
        this.ngZone.run(() => {
          if (result) {
            result.forEach(report => {
              report.SentDate = this.normalizeUtcDate(report.SentDate);
              report.xauditCreatedOn = this.normalizeUtcDate(report.xauditCreatedOn);
              report.xauditModifiedOn = this.normalizeUtcDate(report.xauditModifiedOn);
            });
            this.referralReports = result;
          }
        });
      },
      error => {
        this.ngZone.run(() => {
          this.displayMessage("Error", "Error getting Referral reports.");
        });
      },
      () => {
        this.ngZone.run(() => {
          this.isLoadingReports = false;
          if (!this.destroyed) {
            this.cdr.detectChanges();
          }

          // CL-618/CL-621 follow-up: jump straight into the document viewer for a "Send Link to
          // Doctor Only" magic link, mirroring PatientPortalHomeComponent's deep-link handling.
          // Cleared either way - one-shot for this load, not re-triggered on a later refresh of
          // the same referral (e.g. after a note is added).
          //
          // Falls back to opening targetDocumentId directly when it isn't one of
          // referralReports' rows - a document sent via CL's "Send Link to Doctor Only" TDOx
          // flow isn't registered in TDOS's own Final-Report/correspondence tracking (that's
          // exactly what GetReferralReports's two data sources are scoped to), so it will never
          // appear in this list. openReport only needs a DocumentID to open the viewer, and the
          // magic link's token generation (CaseBelongsToReferringDoctor) already validated this
          // document belongs to this doctor's case before the link was ever minted.
          if (this.targetDocumentId) {
            const match = (this.referralReports ?? []).find(r => r.DocumentID === this.targetDocumentId);
            this.openReport({ model: match ?? { DocumentID: this.targetDocumentId } });
            this.targetDocumentId = null;
          }
        });
      }
    );
  }

  private GetFinalReportNotes() {
    return this.referrerPortalService.getReferreralNotes(this.referrerId, this.currentReferral.ReferralID, "finalreport", this.currentReferral.ReferralSourceType??"1").subscribe(
      result => {
        this.ngZone.run(() => {
          if (result) {
            result.forEach(note => {
              note.CreatedDate = this.normalizeUtcDate(note.CreatedDate);
            });
            this.referralNotes = result;
          }
        });
      },
      error => {
        this.ngZone.run(() => {
          this.displayMessage("Error", "Error getting Referralnotes.");
        });
      },
      () => {
        this.ngZone.run(() => {
          this.isLoadingNotes = false;
          if (!this.destroyed) {
            this.cdr.detectChanges();
          }
        });
      }
    );
  }

  private GetRecallNotes() {
    return this.referrerPortalService.getReferreralNotes(this.referrerId, this.currentReferral.ReferralID, "recall", this.currentReferral.ReferralSourceType??"1").subscribe(
      result => {
        this.ngZone.run(() => {
          if (result) {
            result.forEach(note => {
              note.CreatedDate = this.normalizeUtcDate(note.CreatedDate);
            });
            this.referralRecallNotes = result;
          }
        });
      },
      error => {
        this.ngZone.run(() => {
          this.displayMessage("Error", "Error getting Referral Recall notes.");
        });
      },
      () => {
        this.ngZone.run(() => {
          this.isLoadingRecallNotes = false;
          if (!this.destroyed) {
            this.cdr.detectChanges();
          }
        });
      }
    );
  }

  /** Server sends UTC date strings without a timezone suffix; append 'Z' so Date parses them as UTC instead of local time. */
  private normalizeUtcDate(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.valueOf()) ? null : value;
    }

    const trimmed = value.trimEnd();
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(trimmed);
    const normalized = hasTimezone ? trimmed : trimmed + 'Z';
    const parsed = new Date(normalized);

    return isNaN(parsed.valueOf()) ? null : parsed;
  }

  displayMessage(type, message) {

    if (type.toLowerCase() === "error")
      this.toastr.error(message, null, {timeOut:7000});
    else
      this.toastr.success(message, null, {timeOut:3000});      

  }

  onPrescriptionSelected (event: TdoTableSelectionEvent) {
  }

  onImageSelected (event: TdoTableSelectionEvent) {
    // console.log("Image selected:", event);
  }

  onReportSelected (event: TdoTableSelectionEvent) {
    // console.log("Report selected:", event);
  }

  onNoteSelected (event: TdoTableSelectionEvent) {
  }  
  
  onReferralRecallNoteSelected (event: TdoTableSelectionEvent) {
  }

  getTagValue(validValueId:string){
    return this.staticDataService.getValidValueItemByItemId(validValueId)?.ItemDisplayValue;
  }

  openImage(image:any){
    const parent = this.referralImages.find(i => i.DocumentID === image.ParentDocumentID);
    if (!parent){
      this.displayMessage("Error", "Full size image not found.");
      return;
    }
    let windowOptions = [
      'toolbar=no',
      'location=no',
      'menubar=no',
      'height=' + screen.height,
      'width=' + screen.width,
      'fullscreen=yes' // only works in IE, but here for completeness
    ].join(',');
    const imageHref=`javascript: document.body.innerHTML="<style>div { width:100%; height:100%; background-image: url('${location.origin}${parent.src}'); background-size: contain;  background-repeat: no-repeat;background-position: center;}</style><div></div>"`;
    this.messagingService.openPopUpWindow(imageHref, windowOptions, parent.DocumentFileName, "", null, true, parent.DocumentFileName);
  }

  openReport(report:any, event?: MouseEvent){
    event?.preventDefault();
    event?.stopPropagation();

    if (!report?.model?.DocumentID) {
      this.displayMessage("Error", "Report file was not found.");
      return;
    }

    // Guard against duplicate dialog opens that can leave an overlay/backdrop behind.
    if (this.reportDialogRef) {
      return;
    }

    // console.log("Opening report:", report);

      let documentIds: string[] = [];
      documentIds.push(report.model.DocumentID);
  
      const config = new MatDialogConfig();
      config.panelClass = 'standard-dialog';
      config.data = documentIds;
      config.maxWidth = '100dvw';
      config.maxHeight = '100dvh';
      config.width = '98%';
      config.height = '98%';
      config.autoFocus = false;
      config.restoreFocus = false;
      config.closeOnNavigation = true;
      // console.log('ReferrerPortalReferralDetailComponent openReport:', config);
      this.reportDialogRef = this.dialog.open(TdoOrganizerSlideshowDialogComponent, config);
      this.reportDialogRef.afterClosed().pipe(take(1)).subscribe(() => {
        this.reportDialogRef = null;
      });
  }
}
